import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Button,
  Input,
  Form,
  Select,
  Typography,
  Alert,
  Row,
  Col,
  Statistic,
  Divider,
  Checkbox,
  Spin,
  message,
} from "antd";
import {
  MessageOutlined,
  UserOutlined,
  ScheduleOutlined,
} from "@ant-design/icons";
import { AppContext } from "../context/AppContext";
import axios from "axios";

const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

// Real SMS API implementation
const sendBulkSMS = async (payload) => {
  const { message, recipients } = payload;

  // Convert recipients array to comma-separated string
  const phoneNumbers = recipients.join(",");

  const response = await axios.post(
    "https://app-express.net/kwachaplus/bulk.php",
    new URLSearchParams({
      phone_numbers: phoneNumbers,
      cam_id: payload.company_id || "1",
      message: message,
      userid: payload.user_id,
      sender_id: payload.sender_id,
    }),
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    }
  );

  return response.data;
};

// Normalize phone numbers to 260XXXXXXXXX format
const normalizePhone = (phone) => {
  if (!phone) return null;
  let cleaned = phone.replace(/[^\d]/g, ""); // remove non-digits
  if (cleaned.startsWith("0") && cleaned.length === 10) {
    cleaned = "260" + cleaned.slice(1);
  } else if (cleaned.length === 9 && (cleaned.startsWith("9") || cleaned.startsWith("7"))) {
    cleaned = "260" + cleaned;
  } else if (cleaned.startsWith("260")) {
    // already correct prefix
  } else {
    return null; // unrecognized format
  }
  return cleaned.length === 12 ? cleaned : null;
};

const BulkSMSPage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState("");
  const [result, setResult] = useState(null);
  const [charCount, setCharCount] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);
  const [bulkStats, setBulkStats] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(undefined);
  const [allCompanies, setAllCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const { company } = useContext(AppContext);
  const [user, setUser] = useState();
  const token = localStorage.getItem("token");

  // Fetch user data
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  // Fetch bulk SMS statistics
  useEffect(() => {
    fetchBulkSMSStats();
  }, [company?.id]);

  // Fetch companies for dropdown
  useEffect(() => {
    const fetchAllCompanies = async () => {
      setCompaniesLoading(true);
      try {
        const companiesUrl = `https://zedlenders.pickmesms.com/users?fields=business&filter[role][name][_eq]=Borrower&filter[company]=${company.id}&filter[business][_nnull]=true&limit=1000`;
        const response = await axios.get(companiesUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const uniqueBusinesses = [
          ...new Set(
            response.data.data
              .map((user) => user.business)
              .filter((business) => business && business.trim() !== "")
          ),
        ];
        setAllCompanies(uniqueBusinesses);
      } catch (err) {
        console.error("Error fetching companies:", err);
      } finally {
        setCompaniesLoading(false);
      }
    };
    if (company?.id) {
      fetchAllCompanies();
    }
  }, [company?.id, token]);

  const fetchBulkSMSStats = async () => {
    setStatsLoading(true);
    try {
      if (!company?.id) {
        setStatsLoading(false);
        return;
      }
      const response = await axios.get(
        `https://app-express.net/kwachaplus/get_bulk_sms_stats.php?company_id=${company.id}`
      );

      if (response.data.success) {
        setBulkStats(response.data.data);
      } else {
        console.error("Failed to fetch bulk SMS stats:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching bulk SMS stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Recipient groups with company filtering
  const getRecipientGroups = () => {
    return [
      {
        label: "All Borrowers",
        value: "all_borrowers",
      },
      {
        label: "Active Loan Borrowers",
        value: "active_borrowers",
      },
      {
        label: "Delinquent Borrowers",
        value: "delinquent_borrowers",
      },
      {
        label: `Previous SMS Recipients (${
          bulkStats?.sms_stats?.unique_recipients || 0
        })`,
        value: "previous",
      },
    ];
  };

  const handleSend = async (values) => {
    setLoading(true);
    try {
      // Validate required data
      if (!user?.user_id && !user?.id) {
        message.error("User information not found. Please refresh the page.");
        setLoading(false);
        return;
      }

      // Get phone numbers based on selection
      const recipients = await getRecipients(
        values.recipientType,
        values.customNumbers
      );

      if (recipients.length === 0) {
        message.error("No recipients found. Please check your selection.");
        return;
      }

      // Validate phone numbers
      const invalidNumbers = recipients.filter(
        (num) =>
          !num.startsWith("260") || (num.length !== 11 && num.length !== 12)
      );
      if (invalidNumbers.length > 0) {
        const previewInvalid = invalidNumbers.slice(0, 5).join(", ");
        message.error(
          `Invalid phone numbers found (${invalidNumbers.length}). Example: ${previewInvalid}`
        );
        return;
      }

      const payload = {
        message: values.message,
        recipients,
        user_id: user.user_id ?? user.id,
        company_id: company?.id || "1",
        sender_id: company?.sender_id || "Kwachaplus",
      };

      const response = await sendBulkSMS(payload);

      if (response.success) {
        setResult({
          success: true,
          messageId: response.sms_id,
          recipients: recipients.length,
          creditsUsed: recipients.length,
        });
        form.resetFields();
        message.success(
          `SMS sent successfully to ${recipients.length} recipients!`
        );

        // Refresh SMS statistics after successful send
        fetchBulkSMSStats();
      } else {
        throw new Error(
          response.error || response.message || "SMS sending failed"
        );
      }
    } catch (error) {
      console.error("SMS sending error:", error);
      message.error("Failed to send SMS: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Get recipient phone numbers with company filtering
  const getRecipients = async (type, customNumbers) => {
    if (type === "custom") {
      return customNumbers
        .split(/[\s,]+/)
        .map((num) => num.trim())
        .filter(Boolean)
        .map((num) => normalizePhone(num))
        .filter(Boolean);
    }

    try {
      let url = `https://zedlenders.pickmesms.com/users?fields=phone&filter[role][name][_eq]=Borrower&filter[company]=${company.id}&filter[phone][_nnull]=true&limit=5000`;

      // Add company filter if selected
      if (selectedCompany) {
        url += `&filter[business][_icontains]=${selectedCompany}`;
      }

      // Add specific filters based on recipient type
      switch (type) {
        case "active_borrowers":
          // You might want to add loan status filters here
          break;
        case "delinquent_borrowers":
          // You might want to add delinquency filters here
          break;
        case "previous":
          // For previous SMS recipients, use different approach
          return await getPreviousSMSRecipients();
        default:
          // all_borrowers - no additional filters needed
          break;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data.data
        .map((user) => user.phone)
        .filter((phone) => phone && phone.trim() !== "")
        .map((phone) => normalizePhone(phone))
        .filter(Boolean);
    } catch (error) {
      console.error("Error fetching recipients:", error);
      throw new Error("Failed to fetch recipient phone numbers");
    }
  };

  // Get previous SMS recipients from sentmessages table
  const getPreviousSMSRecipients = async () => {
    try {
      if (!company?.id) {
        return [];
      }
      const response = await axios.get(
        `https://app-express.net/kwachaplus/get_sms_recipients.php?company_id=${company.id}`
      );

      if (response.data.success) {
        return response.data.data.phone_numbers || [];
      } else {
        throw new Error(
          response.data.message || "Failed to fetch previous SMS recipients"
        );
      }
    } catch (error) {
      console.error("Error fetching previous SMS recipients:", error);
      throw new Error("Failed to fetch previous SMS recipients");
    }
  };

  return (
    <div className="bulk-sms-page">
      <Title level={3} className="page-title">
        <MessageOutlined /> Bulk SMS Campaign
      </Title>

      <Row gutter={24}>
        <Col span={16}>
          <Card>
            <Form form={form} onFinish={handleSend} layout="vertical">
              {/* Recipient Selection */}
              {/* Company Filter */}
              <Form.Item label="Filter by Company (Optional)">
                <Select
                  placeholder="Select company to filter recipients"
                  style={{ width: "100%" }}
                  allowClear
                  loading={companiesLoading}
                  value={selectedCompany}
                  onChange={setSelectedCompany}
                >
                  {allCompanies.map((business) => (
                    <Option key={business} value={business}>
                      {business}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Select Recipients"
                name="recipientType"
                rules={[{ required: true }]}
              >
                <Select loading={statsLoading}>
                  {getRecipientGroups().map((group) => (
                    <Option key={group.value} value={group.value}>
                      {group.label}
                      {selectedCompany && ` (${selectedCompany})`}
                    </Option>
                  ))}
                  <Option value="custom">Custom Numbers</Option>
                </Select>
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prev, current) =>
                  prev.recipientType !== current.recipientType
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue("recipientType") === "custom" && (
                    <Form.Item
                      name="customNumbers"
                      label="Enter Phone Numbers (One per line)"
                      rules={[{ required: true }]}
                    >
                      <TextArea
                        rows={4}
                        placeholder="260971234567\n260963456789"
                      />
                    </Form.Item>
                  )
                }
              </Form.Item>

              {/* Message Composition */}
              <Form.Item
                label="SMS Message"
                name="message"
                rules={[
                  { required: true },
                  { max: 160, message: "SMS cannot exceed 160 characters" },
                ]}
              >
                <TextArea
                  rows={4}
                  showCount
                  maxLength={160}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  placeholder="Hi {name}, your loan payment of ZMW {amount} is due on {date}..."
                />
              </Form.Item>

              {/* Message Templates */}
              <Form.Item label="Templates">
                <Select
                  placeholder="Select a template"
                  onChange={(value) => form.setFieldsValue({ message: value })}
                >
                  <Option value="Payment Reminder">
                    Payment Reminder - {charCount}/160
                  </Option>
                  <Option value="Loan Approval Notice">
                    Loan Approval Notice - {charCount}/160
                  </Option>
                  <Option value="Delinquency Alert">
                    Delinquency Alert - {charCount}/160
                  </Option>
                </Select>
              </Form.Item>

              {/* Scheduling */}
              <Form.Item
                label="Schedule"
                name="schedule"
                initialValue="immediate"
              >
                <Select>
                  <Option value="immediate">Send Immediately</Option>
                  <Option value="schedule">Schedule for Later</Option>
                </Select>
              </Form.Item>

              {/* Preview and Send */}
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<MessageOutlined />}
                >
                  Send SMS Campaign
                </Button>

                <Button
                  style={{ marginLeft: 16 }}
                  onClick={() => setPreview(form.getFieldValue("message"))}
                >
                  Preview Message
                </Button>
              </Form.Item>
            </Form>
          </Card>

          {/* Preview Section */}
          {preview && (
            <Card title="Message Preview" style={{ marginTop: 24 }}>
              <div className="sms-preview">
                <div className="sms-content">{preview}</div>
                <div className="sms-meta">
                  Length: {preview.length}/160 characters
                </div>
              </div>
            </Card>
          )}
        </Col>

        {/* Statistics Sidebar */}
        <Col span={8}>
          <Card title="SMS Statistics">
            {statsLoading ? (
              <Spin />
            ) : (
              <>
                <Statistic
                  title="Available SMS Credits"
                  value={bulkStats?.available_credits || 0}
                  suffix={<ScheduleOutlined />}
                />
              </>
            )}
          </Card>

          {false && (
            <Card title="Quick Actions" style={{ marginTop: 24 }}>
              <Button block icon={<UserOutlined />}>
                Upload Contact List
              </Button>
              <Button block icon={<ScheduleOutlined />} style={{ marginTop: 16 }}>
                Schedule Campaign
              </Button>
            </Card>
          )}
        </Col>
      </Row>

      {/* Result Display */}
      {result && (
        <Alert
          style={{ marginTop: 24 }}
          message={
            <>
              SMS Campaign Sent Successfully!
              <ul>
                <li>Message ID: {result.messageId}</li>
                <li>Recipients: {result.recipients}</li>
                <li>Credits Used: {result.creditsUsed}</li>
              </ul>
            </>
          }
          type="success"
          showIcon
        />
      )}
    </div>
  );
};

export default BulkSMSPage;
